import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Pastilla de ESTATUS del estándar (decisión de Eduardo, 2026-10-29): `rounded-full`, punto de color
 * + texto, fondo muy tenue del tono y borde fino del mismo color que el texto. Un solo estilo para
 * todo estado de solo lectura (Estado, Validación, Al corriente, Liquidada...). El color nunca es el
 * único significado: el texto dice el estado. Lo que se elige (filtros, selector de comprador) NO es
 * una pastilla: es `ui/filter-chips` (`FilterChips`).
 *
 * Las pantallas sin migrar usan `Badge` de shadcn con `variant="default"` para todo, lo que pinta de
 * verde sólido tanto un "Activo" como un "Automático". Aquí el tono dice el estado; para un valor de
 * catálogo (tipo, categoría) va `ModalTablePill`.
 */
export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral";

/**
 * Todos los tonos que acepta la pastilla. `info` es el verde de marca (`--primary`), no azul; para
 * estados que necesitan más colores (etapas de Legal Flow, estatus de propiedad) están `blue`
 * (`--status-info`), `purple` (`--status-purple`), `teal` (el de "Firma titular") y `amber`
 * (`--status-warning`, más naranja que el amarillo de `warning`), con el mismo estilo que los
 * demás. Van aparte de `StatusTone` para no romper los `Record<StatusTone, …>` que ya existen
 * (p. ej. `BARRA_TONO` de citas).
 */
export type StatusPillTone = StatusTone | "blue" | "purple" | "teal" | "amber";

const TONE_CLS: Record<StatusPillTone, { wrap: string; dot: string }> = {
  success: { wrap: "bg-emerald-50/60 text-emerald-700 border-emerald-600/40 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-400/40", dot: "bg-emerald-600 dark:bg-emerald-400" },
  warning: { wrap: "bg-amber-50/60 text-amber-700 border-amber-600/40 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-400/40", dot: "bg-amber-500" },
  danger: { wrap: "bg-red-50/60 text-red-700 border-red-600/40 dark:bg-red-500/10 dark:text-red-300 dark:border-red-400/40", dot: "bg-red-600 dark:bg-red-400" },
  info: { wrap: "bg-primary/5 text-primary border-primary/40", dot: "bg-primary" },
  neutral: { wrap: "bg-slate-50/60 text-slate-600 border-slate-400/50 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-400/40", dot: "bg-slate-400" },
  blue: { wrap: "bg-sky-50/60 text-sky-700 border-sky-600/40 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-400/40", dot: "bg-sky-500" },
  purple: { wrap: "bg-violet-50/60 text-violet-700 border-violet-600/40 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-400/40", dot: "bg-violet-500" },
  teal: { wrap: "bg-teal-50/60 text-teal-700 border-teal-600/40 dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-400/40", dot: "bg-teal-500" },
  amber: { wrap: "bg-orange-50/60 text-orange-700 border-orange-600/40 dark:bg-orange-500/10 dark:text-orange-300 dark:border-orange-400/40", dot: "bg-orange-500" },
};

export interface StatusPillProps {
  tone?: StatusPillTone;
  /**
   * "rect" (`rounded-md`) es la forma de la escala del panel (`escala-panel.ts`): chips y estados
   * rectangulares, el círculo solo para el punto. "pill" se conserva como default para no cambiar
   * los 44 usos existentes sin revisarlos; las pantallas nuevas o rehechas pasan `shape="rect"`.
   */
  shape?: "pill" | "rect";
  children: ReactNode;
  /** Deprecado y sin efecto: el estilo del estándar siempre lleva punto. Se acepta para no romper llamadas viejas. */
  hideDot?: boolean;
  className?: string;
  title?: string;
}

// forwardRef y props del span: se usa como disparador de InfoTooltip (Radix asChild necesita el ref).
export const StatusPill = forwardRef<HTMLSpanElement, StatusPillProps & Omit<HTMLAttributes<HTMLSpanElement>, keyof StatusPillProps>>(
  function StatusPill({ tone = "neutral", shape = "pill", children, hideDot: _hideDot, className, ...rest }, ref) {
    const t = TONE_CLS[tone];
    return (
      <span
        ref={ref}
        {...rest}
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 whitespace-nowrap border px-2 py-0.5 text-[11px] font-medium leading-4",
          shape === "rect" ? "rounded-md" : "rounded-full",
          t.wrap,
          className,
        )}
      >
        <span className={cn("size-1.5 shrink-0 rounded-full", t.dot)} aria-hidden />
        <span className="truncate">{children}</span>
      </span>
    );
  },
);
